import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { copyContact } from "@/lib/copyContact";
import { Plus, Search, Pencil, Trash2, Mail, Phone, User, Loader2, Building2, Factory, MapPin, Check, Smartphone, Copy } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { toast } from "sonner";

const LOCATIONS = [
  { key: "main_office", label: "Main Office", icon: Building2, addrKey: "main_office_address" },
  { key: "factory", label: "Factory", icon: Factory, addrKey: "factory_address" },
];
const locLabel = (k) => LOCATIONS.find((l) => l.key === k)?.label || "Unassigned";

const emptyPerson = () => ({ name: "", role: "", email: "", phone: "", phone_mobile: "", notes: "", location: "main_office" });

function PersonDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(emptyPerson());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...emptyPerson(), ...initial } : emptyPerson());
  }, [open, initial]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const res = initial?.id ? await api.put(`/company-directory/${initial.id}`, form) : await api.post("/company-directory", form);
      onSaved(res.data, !!initial?.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const label = "text-xs font-semibold uppercase tracking-[0.1em] text-slate-600";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="person-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit contact" : "New contact"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label className={label}>Name</Label>
              <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Jane Doe" className="h-9 rounded-sm" data-testid="person-name-input" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label className={label}>Role</Label>
              <Input value={form.role} onChange={(e) => setField("role", e.target.value)} placeholder="Plant Manager" className="h-9 rounded-sm" data-testid="person-role-input" />
            </div>
            <div className="space-y-1.5">
              <Label className={label}>Location</Label>
              <Select value={form.location || undefined} onValueChange={(v) => setField("location", v)}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="person-location-select"><span>{locLabel(form.location)}</span></SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map((l) => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className={label}>Email</Label>
              <Input value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="jane@company.com" className="h-9 rounded-sm" data-testid="person-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className={label}>Mobile phone</Label>
              <Input value={form.phone_mobile || ""} onChange={(e) => setField("phone_mobile", e.target.value)} placeholder="555-1000" className="h-9 rounded-sm" data-testid="person-phone-mobile-input" />
            </div>
            <div className="space-y-1.5">
              <Label className={label}>Office phone</Label>
              <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="555-2000 ext. 12" className="h-9 rounded-sm" data-testid="person-phone-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className={label}>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Department, extension, shift, etc." className="rounded-sm" data-testid="person-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="person-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="person-save">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CompanyDirectory({ initialQuery = "" }) {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(initialQuery);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [toDelete, setToDelete] = useState(null);
  const [addresses, setAddresses] = useState({ main_office_address: "", factory_address: "" });
  const [editAddr, setEditAddr] = useState(null); // { key, value }

  useEffect(() => { if (initialQuery) setQ(initialQuery); }, [initialQuery]);

  useEffect(() => {
    Promise.all([api.get("/company-directory"), api.get("/company-directory/locations")])
      .then(([r, loc]) => { setPeople(r.data); setAddresses(loc.data); })
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const saveAddress = async () => {
    const next = { ...addresses, [editAddr.key]: editAddr.value.trim() };
    try {
      const { data } = await api.put("/company-directory/locations", next);
      setAddresses(data);
      setEditAddr(null);
      toast.success("Address saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return people;
    return people.filter((p) => `${p.name} ${p.role} ${p.email}`.toLowerCase().includes(term));
  }, [people, q]);

  const onSaved = (p, isEdit) => {
    setPeople((prev) => {
      const next = isEdit ? prev.map((x) => (x.id === p.id ? p : x)) : [...prev, p];
      return next.sort((a, b) => a.name.localeCompare(b.name));
    });
    toast.success(isEdit ? "Contact updated" : "Contact added");
  };

  const del = async () => {
    try {
      await api.delete(`/company-directory/${toDelete.id}`);
      setPeople((prev) => prev.filter((x) => x.id !== toDelete.id));
      toast.success("Contact deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
    setToDelete(null);
  };

  return (
    <div className="mx-auto max-w-[1100px] px-6 py-8 md:px-10 md:py-12" data-testid="company-directory-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">Company Directory</h1>
          <p className="mt-1 text-sm text-slate-500">Your internal team contacts.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search people…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-56 rounded-sm pl-8" data-testid="person-search" />
          </div>
          <Button onClick={() => setDialog({ open: true, initial: null })} className="h-9 rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="add-person-btn">
            <Plus className="mr-1 h-4 w-4" /> Add contact
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-sm border border-dashed border-slate-300 py-20 text-center" data-testid="company-directory-empty">
          <User className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">{q ? "No people match your search." : "No contacts yet. Add your first team member."}</p>
        </div>
      ) : (
        <div className="space-y-10">
          {(() => {
            const renderCard = (p) => (
            <div key={p.id} className="group flex flex-col rounded-sm border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md" data-testid={`person-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#002FA7]/10 text-sm font-bold text-[#002FA7]">
                    {(p.name || "?").trim().charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h3 className="truncate font-heading text-base font-semibold text-slate-900">{p.name}</h3>
                    {p.role && <p className="truncate text-xs font-medium text-slate-500">{p.role}</p>}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button onClick={() => copyContact({ name: p.name, title: p.role, email: p.email, phone: p.phone, phone_mobile: p.phone_mobile })} className="rounded-sm p-1.5 text-slate-400 hover:bg-slate-100 hover:text-[#002FA7]" data-testid={`copy-person-${p.id}`} title="Copy contact"><Copy className="h-4 w-4" /></button>
                  <button onClick={() => setDialog({ open: true, initial: p })} className="rounded-sm p-1.5 text-slate-400 hover:bg-slate-100 hover:text-[#002FA7]" data-testid={`edit-person-${p.id}`} title="Edit"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => setToDelete(p)} className="rounded-sm p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`delete-person-${p.id}`} title="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {(p.email || p.phone || p.phone_mobile) && (
                <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-500">
                  {p.email && <a href={`mailto:${p.email}`} className="flex items-center gap-1.5 hover:text-[#002FA7]"><Mail className="h-3 w-3 shrink-0" /> <span className="truncate">{p.email}</span></a>}
                  {p.phone_mobile && <a href={`tel:${p.phone_mobile}`} className="flex items-center gap-1.5 hover:text-[#002FA7]"><Smartphone className="h-3 w-3 shrink-0" /> {p.phone_mobile} <span className="text-[10px] text-slate-400">Mobile</span></a>}
                  {p.phone && <a href={`tel:${p.phone}`} className="flex items-center gap-1.5 hover:text-[#002FA7]"><Phone className="h-3 w-3 shrink-0" /> {p.phone} <span className="text-[10px] text-slate-400">Office</span></a>}
                </div>
              )}
              {p.notes && <p className="mt-2 text-xs italic text-slate-400">{p.notes}</p>}
            </div>
            );
            const unassigned = filtered.filter((p) => !LOCATIONS.some((l) => l.key === (p.location || "")));
            return (
              <>
                {LOCATIONS.map((loc) => {
                  const group = filtered.filter((p) => (p.location || "") === loc.key);
                  const editing = editAddr?.key === loc.addrKey;
                  return (
                    <section key={loc.key} data-testid={`location-section-${loc.key}`}>
                      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-b-2 border-[#002FA7] pb-2">
                        <span className="flex items-center gap-2 font-heading text-lg font-bold tracking-tight text-slate-900">
                          <loc.icon className="h-5 w-5 text-[#002FA7]" strokeWidth={1.5} /> {loc.label}
                          <span className="text-sm font-normal text-slate-400">{group.length}</span>
                        </span>
                        {editing ? (
                          <span className="flex flex-1 items-center gap-1.5">
                            <Input value={editAddr.value} onChange={(e) => setEditAddr((s) => ({ ...s, value: e.target.value }))} placeholder="Street, city, state, zip" className="h-8 max-w-md rounded-sm text-xs" data-testid={`address-input-${loc.key}`} autoFocus onKeyDown={(e) => { if (e.key === "Enter") saveAddress(); }} />
                            <button onClick={saveAddress} className="rounded-sm bg-[#002FA7] p-1.5 text-white hover:bg-[#002080]" data-testid={`address-save-${loc.key}`} title="Save address"><Check className="h-3.5 w-3.5" /></button>
                          </span>
                        ) : (
                          <button onClick={() => setEditAddr({ key: loc.addrKey, value: addresses[loc.addrKey] || "" })} className="group/addr flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#002FA7]" data-testid={`address-display-${loc.key}`} title="Edit address">
                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                            {addresses[loc.addrKey] || <span className="italic text-slate-400">Add address…</span>}
                            <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover/addr:opacity-100" />
                          </button>
                        )}
                      </div>
                      {group.length === 0 ? (
                        <p className="py-4 text-sm text-slate-400" data-testid={`location-empty-${loc.key}`}>No contacts at this location yet.</p>
                      ) : (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">{group.map(renderCard)}</div>
                      )}
                    </section>
                  );
                })}
                {unassigned.length > 0 && (
                  <section data-testid="location-section-unassigned">
                    <div className="mb-3 flex items-center gap-2 border-b border-slate-200 pb-2">
                      <User className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
                      <span className="font-heading text-lg font-bold tracking-tight text-slate-600">Unassigned</span>
                      <span className="text-sm text-slate-400">{unassigned.length}</span>
                      <span className="text-xs text-slate-400">— edit each contact to pick a location</span>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">{unassigned.map(renderCard)}</div>
                  </section>
                )}
              </>
            );
          })()}
        </div>
      )}

      <PersonDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} initial={dialog.initial} onSaved={onSaved} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete contact?</AlertDialogTitle>
            <AlertDialogDescription>This removes “{toDelete?.name}” from the directory. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="confirm-delete-person">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
